import React from 'react';
import { useAuth } from '../../auth/AuthProvider';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import { styles } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatTL = (value) => {
  if (value == null) return 'Veri yok';
  return Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' TL';
};

const OperasyonDashboard = () => {
  const { user } = useAuth();
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: talepler = [],
    isLoading: taleplerLoading,
    isValidating: taleplerValidating
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'operasyon', 'talepler'],
    async () => {
      const res = await fetch(`${API_URL}/api/talepler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: siparisler = [],
    isLoading: siparislerLoading,
    isValidating: siparislerValidating
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'operasyon', 'siparisler'],
    async () => {
      const res = await fetch(`${API_URL}/api/siparisler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: summary,
    isLoading: summaryLoading,
    isValidating: summaryValidating
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'operasyon', 'summary'],
    async () => {
      const res = await fetch(`${API_URL}/api/dashboard/summary`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    },
    { staleTime: 20000 }
  );

  const loading = taleplerLoading || siparislerLoading || summaryLoading;
  const isValidating = taleplerValidating || siparislerValidating || summaryValidating;

  const lokasyonCirolar = summary?.pos?.lokasyon_cirolar || [];
  const toplamCiro = lokasyonCirolar.length > 0
    ? lokasyonCirolar.reduce((sum, l) => sum + (l.net_ciro || 0), 0)
    : null;
  const eslestirmeOrani = summary?.pos?.eslestirme_orani;

  return (
    <div>
      <Header title="Operasyon Genel Panosu" />
      <div style={{ padding: '20px', background: '#f5f5f5', minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', minHeight: '20px' }}>
          {isValidating && !loading && (
            <span style={{ fontSize: '12px', color: '#666', background: '#f1f3f5', borderRadius: '12px', padding: '4px 10px' }}>
              Güncelleniyor...
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <KpiCard title="Günlük Satış" value={loading ? '...' : formatTL(toplamCiro)} color={styles.success} icon="money" />
          <KpiCard title="Bekleyen Talep" value={loading ? '...' : talepler.filter(t => t.durum === 'BEKLENIYOR').length} color={styles.warning} icon="clipboard" />
          <KpiCard title="Aktif Sipariş" value={loading ? '...' : siparisler.length} color={styles.info} icon="package" />
          <KpiCard title="POS Eşleşme" value={loading ? '...' : (eslestirmeOrani != null ? `%${eslestirmeOrani.toFixed(1)}` : 'Veri yok')} color={styles.primary} icon="factory" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>Günlük Özet</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, row) => (
                  <div key={`skeleton-${row}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: '40%' }} />
                    <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: '30%' }} />
                  </div>
                ))
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <span>Toplam Satış</span><strong>{formatTL(toplamCiro)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <span>7 Günlük Toplam</span><strong>{formatTL(summary?.pos?.yedi_gun_toplam)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <span>POS Eşleşme Oranı</span><strong>{eslestirmeOrani != null ? `%${eslestirmeOrani.toFixed(1)}` : 'Veri yok'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <span>Yeni Talep</span><strong>{talepler.filter(t => t.durum === 'BEKLENIYOR').length} adet</strong>
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>Sube Performansi</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              {loading ? (
                Array.from({ length: 3 }).map((_, row) => (
                  <div key={`branch-skeleton-${row}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: '40%' }} />
                    <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: '25%' }} />
                  </div>
                ))
              ) : lokasyonCirolar.length > 0 ? (
                lokasyonCirolar.map((lok) => (
                  <div key={lok.lokasyon_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <span>{lok.lokasyon_ad}</span>
                    <strong>{formatTL(lok.net_ciro)}</strong>
                  </div>
                ))
              ) : (
                <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px', textAlign: 'center', color: '#999' }}>
                  Veri yok
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperasyonDashboard;
